import type { DisputePlaybook } from "@/api/deduction.tsx"

export type DisputeDeliveryMethod = "email" | "portal"

/**
 * How a dispute filed from the drawer reaches the retailer.
 *
 * A category-level address always means email (that is how the server routes it). Otherwise the
 * retailer's playbook decides: a retailer with an email channel gets the dispute mailed, a
 * portal-only retailer (Costco, Kroger, most of Target) has the dispute recorded here for the
 * person to file in the portal. With neither, the legacy assumption is a portal integration.
 */
export function dispute_delivery_method(
  category_dispute_email_to: string | null | undefined,
  playbook: DisputePlaybook | null | undefined,
): DisputeDeliveryMethod {
  if (category_dispute_email_to?.trim()) return "email"
  if (playbook?.channel === "email" && playbook.dispute_email) return "email"
  return "portal"
}

/** The mailbox the drawer should say the dispute goes to, if it goes by email. */
export function dispute_recipient(
  category_dispute_email_to: string | null | undefined,
  playbook: DisputePlaybook | null | undefined,
): string | null {
  const category = category_dispute_email_to?.trim()
  if (category) return category
  if (playbook?.channel === "email" && playbook.dispute_email) return playbook.dispute_email
  return null
}

/**
 * One-line microcopy for the File Dispute / Request Backup buttons: where the request goes and
 * whether MarginWiz sends it or only records it.
 */
export function playbook_action_hint(
  playbook: DisputePlaybook | null | undefined,
  kind: "file_dispute" | "request_backup",
  category_dispute_email_to?: string | null,
): string | undefined {
  if (!playbook) return undefined
  if (kind === "request_backup") {
    if (playbook.backup_email) {
      return `Emails ${playbook.retailer}'s backup desk at ${playbook.backup_email}.`
    }
    if (playbook.backup_steps.length > 0) {
      const where = playbook.portal_name ?? `the ${playbook.retailer} portal`
      return `${playbook.retailer} does not take backup requests by email — pull it from ${where}. See "How to dispute" below for the steps.`
    }
    return undefined
  }
  const recipient = dispute_recipient(category_dispute_email_to, playbook)
  if (recipient) {
    return `Emails the dispute to ${recipient}.`
  }
  const where = playbook.portal_name ?? `the ${playbook.retailer} portal`
  return `${playbook.retailer} takes disputes only through ${where}. MarginWiz records the dispute here; file it there following the steps in "How to dispute".`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const kroger_r: DisputePlaybook = {
    retailer: "Kroger",
    deduction_type: "PRGX post-audit claim (R)",
    channel: "portal",
    dispute_email: "krogercorrespondence@prgx.com",
    backup_email: "kroger.east@prgx.com",
    portal_name: "Supplier Connect (Lavante)",
    portal_url: "https://partnerpass.krogerapps.com",
    steps: ["File through Supplier Connect."],
    backup_steps: ["Open the Kroger Backup Portal."],
    notes: [],
  }
  const five_below: DisputePlaybook = {
    retailer: "Five Below",
    deduction_type: null,
    channel: "email",
    dispute_email: "invoices@fivebelow.com",
    backup_email: "accountspayable@fivebelow.com",
    portal_name: null,
    portal_url: null,
    steps: [],
    backup_steps: [],
    notes: [],
  }
  const costco: DisputePlaybook = { ...kroger_r, retailer: "Costco", dispute_email: null, backup_email: null, portal_name: "Vendor Accounting Portal (Costco Hub)" }

  describe("dispute_delivery_method", () => {
    it("category address always wins and means email", () => {
      expect(dispute_delivery_method("disputes@kehe.com", null)).toBe("email")
      expect(dispute_delivery_method("  vendor@example.com  ", costco)).toBe("email")
    })
    it("uses the playbook channel when no category address is set", () => {
      expect(dispute_delivery_method(null, five_below)).toBe("email")
      expect(dispute_delivery_method(null, kroger_r)).toBe("portal")
      expect(dispute_delivery_method(null, costco)).toBe("portal")
    })
    it("falls back to portal with neither", () => {
      expect(dispute_delivery_method(null, null)).toBe("portal")
      expect(dispute_delivery_method("   ", undefined)).toBe("portal")
    })
  })

  describe("dispute_recipient", () => {
    it("never names a portal retailer's contact as the recipient", () => {
      // krogercorrespondence@ is a contact worth showing, not where the drawer's dispute goes.
      expect(dispute_recipient(null, kroger_r)).toBeNull()
      expect(dispute_recipient(null, five_below)).toBe("invoices@fivebelow.com")
      expect(dispute_recipient("cat@example.com", kroger_r)).toBe("cat@example.com")
    })
  })

  describe("playbook_action_hint", () => {
    it("names the backup mailbox when one exists", () => {
      expect(playbook_action_hint(kroger_r, "request_backup")).toContain("kroger.east@prgx.com")
    })
    it("points portal-only backup at the portal", () => {
      expect(playbook_action_hint(costco, "request_backup")).toContain("Vendor Accounting Portal")
    })
    it("explains that portal disputes are recorded, not sent", () => {
      expect(playbook_action_hint(kroger_r, "file_dispute")).toContain("records the dispute")
      expect(playbook_action_hint(five_below, "file_dispute")).toContain("invoices@fivebelow.com")
      expect(playbook_action_hint(kroger_r, "file_dispute", "cat@example.com")).toContain("cat@example.com")
    })
    it("is silent without a playbook", () => {
      expect(playbook_action_hint(null, "file_dispute")).toBeUndefined()
    })
  })
}
